import { useState } from 'react'
import { Button } from '../ui/Button'
import { X, AlertTriangle, Loader2 } from 'lucide-react'
import BranchService from '../../services/branchService'
import AppURLs from '../../config/urls' // Import AppURLs for debugging

const DeleteConfirmationModal = ({ isOpen, onClose, onBranchDeleted, branch }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Handle delete confirmation
  const handleDelete = async () => {
    if (!branch?._id) {
      console.error('❌ No branch ID provided for deletion')
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      // Enhanced debug logging
      console.log('🗑️ Deleting branch:', {
        branchId: branch._id,
        branchName: branch.name,
        deleteURL: `${AppURLs.API}/branches/${branch._id}`,
        token: localStorage.getItem('tezlaa-access-token') ? 'Present' : 'Missing'
      })
      
      const response = await BranchService.deleteBranch(branch._id)
      
      if (response.success) {
        console.log('✅ Branch deleted successfully:', response)
        
        // Notify parent component
        onBranchDeleted(branch._id)
        
        // Close modal
        onClose()
      } else {
        console.log('⚠️ Delete failed with response:', response)
        setError(response.message || 'Failed to delete branch')
      }
    } catch (error) {
      console.error('❌ Error deleting branch:', error)
      
      // Enhanced error handling
      let errorMessage = 'Failed to delete branch. Please try again.'
      
      if (error.message.includes('Not found')) {
        errorMessage = 'Branch not found. It may have already been deleted.'
      } else if (error.message.includes('Unauthorized')) {
        errorMessage = 'You are not authorized to delete this branch. Please login again.'
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your connection and try again.'
      } else if (error.message.includes('conflict') || error.message.includes('in use')) {
        errorMessage = 'Cannot delete branch. It may be in use by other records.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Handle modal close
  const handleClose = () => {
    if (!loading) {
      console.log('🔒 Closing delete modal')
      setError(null)
      onClose()
    }
  }

  if (!isOpen || !branch) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Delete Confirmation</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="p-1 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Confirmation Message */}
          <div className="mb-6">
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete this branch? This action cannot be undone.
            </p>
            
            {/* Branch Details */}
            <div className="bg-gray-50 rounded-lg p-4 mt-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-500">Branch Name:</span>
                  <span className="text-sm text-gray-900">{branch.name || 'Unknown'}</span>
                </div>
                {branch.code && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Code:</span>
                    <span className="text-sm text-gray-900">{branch.code}</span>
                  </div>
                )}
                {branch.type && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Type:</span>
                    <span className="text-sm text-gray-900 capitalize">{branch.type}</span>
                  </div>
                )}
                {branch.email && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Email:</span>
                    <span className="text-sm text-gray-900">{branch.email}</span>
                  </div>
                )}
                {branch.phoneNumber && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-500">Phone:</span>
                    <span className="text-sm text-gray-900">{branch.phoneNumber}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Warning Message */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-yellow-700">
                <strong>Warning:</strong> Deleting this branch will permanently remove all associated data including:
              </p>
              <ul className="text-sm text-yellow-700 mt-2 ml-4 list-disc">
                <li>Branch configuration and settings</li>
                <li>Historical transaction records</li>
                <li>User assignments to this branch</li>
                <li>Inventory and stock data</li>
              </ul>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                'Delete Branch'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeleteConfirmationModal